import BoltIcon from "@mui/icons-material/Bolt";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import { darken, lighten, type Theme } from "@mui/material/styles";
import type { ReactElement } from "react";

export type RecommendationId = "cna" | "rn" | "weekend_rn" | "weekend_cna" | "lpn" | "weekend_lpn";
export type WageKey = "rn" | "cna" | "lpn" | "weekendRn" | "weekendCna" | "weekendLpn";

// Categorical accent per recommendation — theme-derived so the levers retint with
// the app's chosen palette instead of the old hard-coded #6B5CE7/#9333EA/... hexes.
// The six levers stay in one cohesive family: CNA/RN on the brand primary and a
// darkened shade of it, the weekend variants on lighter/darker brand shades, and LPN
// on the status-info blue. All derive from `theme.palette` so they track the app's
// chosen palette AND stay contrast-safe on white (unlike the gold `secondary`, which
// failed WCAG on thin sliders / small text).
export type RecColorKey = "brand" | "brandDark" | "brandDeep" | "brandSoft" | "info";

export const recColor = (theme: Theme, key: RecColorKey): string => {
	const brand = theme.palette.primary.main;
	switch (key) {
		case "brandDark":
			return darken(brand, 0.22);
		case "brandDeep":
			return darken(brand, 0.4);
		case "brandSoft":
			// A readable mid shade — lighten only slightly so it stays legible on white.
			return lighten(darken(brand, 0.1), 0.05);
		case "info":
			return theme.palette.info.main;
		default:
			return brand;
	}
};

export type RecommendationType = {
	id: RecommendationId;
	titleKey: string;
	descKey: string;
	wageKey: WageKey; // which dept wage from PbjDeptWages drives slider math
	impactedIds: string[]; // category ids affected
	// Which impacted category drives the headline `+X.XX HPPD` label and the
	// `spend = primary_gap × wage` slider relationship. Mirrors the BE
	// `PBJ_ACTION_PRIMARY_CATEGORY` mapping so the FE projection math matches
	// the greedy. "1"=Total, "2"=RN, "3"=Weekend.
	primaryCategoryId: string;
	icon: ReactElement;
	colorKey: RecColorKey; // theme-derived accent (resolve via recColor(theme, colorKey))
	// Sustained-rate action label, e.g. "Schedule {{hours}} more hours of CNA a day".
	actionKey: string;
	// Catch-up-rate action label, e.g. "Schedule {{hours}} more hours of CNA per
	// remaining day". Used when the "Quarter to date" period mode is active.
	actionCatchupKey: string;
	// i18n key for the hover-math tooltip on this rec's icon+title. Interpolates
	// {{days}}, {{weekendDays}}, {{ratio}}, {{census}} so the tooltip shows the
	// real numbers for the current period.
	mathKey: string;
	// True for actions that only land on weekend days (weekend_cna/weekend_rn) —
	// drives which `*Remaining` day count the FE uses when computing catch-up math
	// for slider-driven adjustments.
	isWeekend: boolean;
};

// Per-HPPD-category breakdown for a suggested plan item (BE-supplied, enriched
// with the category title in `handleSuggestPlan`). Absent on manually-added items.
export type PlanItemCategoryBreakdown = {
	categoryId: string;
	categoryTitle: string;
	hppdGap: number;
	points: number;
};

// Hour-rate and action-label variants per period mode. The plan item stores both
// so ActionItems can swap the displayed value on toggle without re-running suggest.
export type PlanItem = {
	id: RecommendationId;
	titleKey: string;
	spend: number;
	impact: number; // HPPD gain (headline — primary category of the action)
	points: number; // total across all coupled categories
	// Sustained per-applicable-day rate (full-quarter view).
	hours: number;
	// Catch-up per-remaining-applicable-day rate. null when no remaining days
	// of the applicable type (e.g. quarter is over, or a weekend rec when there
	// are no weekend days left). When null, ActionItems shows "—" in QTD mode.
	hoursCatchup: number | null;
	// Sustained-rate i18n key, e.g. "Schedule {{hours}} more hours of CNA a day".
	actionKey: string;
	// Catch-up i18n key, e.g. "Schedule {{hours}} more hours of CNA per remaining day".
	actionCatchupKey: string;
	// Populated for every plan item — both BE Suggest Plan greedy suggestions and
	// manual (slider-added) items. Itemizes the cross-category point + HPPD
	// contributions, carrying the per-category hppdGap the cumulative-scoring pass
	// walks the band ladder with.
	breakdown?: PlanItemCategoryBreakdown[];
};

// The 6 manually-overridable assumed wages, in dialog order. `overrideKey` is the
// PbjModelUpsertBody field that stores the override (null = fall back to computed);
// `computedKey` reads the scorer-derived wage from prediction.deptWages for the seed
// value + placeholder; `labelKey`/`testIdSuffix` drive the field label + data-testid.
export type WageOverrideField = {
	overrideKey: "wageOverrideRn" | "wageOverrideCna" | "wageOverrideLpn" | "wageOverrideWeekendRn" | "wageOverrideWeekendCna" | "wageOverrideWeekendLpn";
	computedKey: WageKey;
	labelKey: string;
	testIdSuffix: string;
};

export const WAGE_OVERRIDE_FIELDS: WageOverrideField[] = [
	{ overrideKey: "wageOverrideRn", computedKey: "rn", labelKey: "pbj_model.recommendations.wage_overrides.label_rn", testIdSuffix: "rn" },
	{ overrideKey: "wageOverrideCna", computedKey: "cna", labelKey: "pbj_model.recommendations.wage_overrides.label_cna", testIdSuffix: "cna" },
	{ overrideKey: "wageOverrideLpn", computedKey: "lpn", labelKey: "pbj_model.recommendations.wage_overrides.label_lpn", testIdSuffix: "lpn" },
	{
		overrideKey: "wageOverrideWeekendRn",
		computedKey: "weekendRn",
		labelKey: "pbj_model.recommendations.wage_overrides.label_weekend_rn",
		testIdSuffix: "weekend-rn",
	},
	{
		overrideKey: "wageOverrideWeekendCna",
		computedKey: "weekendCna",
		labelKey: "pbj_model.recommendations.wage_overrides.label_weekend_cna",
		testIdSuffix: "weekend-cna",
	},
	{
		overrideKey: "wageOverrideWeekendLpn",
		computedKey: "weekendLpn",
		labelKey: "pbj_model.recommendations.wage_overrides.label_weekend_lpn",
		testIdSuffix: "weekend-lpn",
	},
];

export const RECOMMENDATION_TYPES: RecommendationType[] = [
	{
		id: "cna",
		titleKey: "pbj_model.recommendations.cna.title",
		descKey: "pbj_model.recommendations.cna.desc",
		wageKey: "cna",
		impactedIds: ["1"],
		primaryCategoryId: "1",
		icon: <PeopleIcon sx={{ fontSize: 16 }} />,
		colorKey: "brand",
		actionKey: "pbj_model.recommendations.cna.action",
		actionCatchupKey: "pbj_model.recommendations.cna.action_catchup",
		mathKey: "pbj_model.recommendations.cna.math",
		isWeekend: false,
	},
	{
		id: "rn",
		titleKey: "pbj_model.recommendations.rn.title",
		descKey: "pbj_model.recommendations.rn.desc",
		wageKey: "rn",
		impactedIds: ["1", "2"],
		primaryCategoryId: "2",
		icon: <BoltIcon sx={{ fontSize: 16 }} />,
		colorKey: "brandDark",
		actionKey: "pbj_model.recommendations.rn.action",
		actionCatchupKey: "pbj_model.recommendations.rn.action_catchup",
		mathKey: "pbj_model.recommendations.rn.math",
		isWeekend: false,
	},
	{
		// Weekend RN: extra RN hours on weekends. Counts toward Total HPPD, RN HPPD,
		// and Weekend HPPD because RN hours feed all three CMS categories.
		id: "weekend_rn",
		titleKey: "pbj_model.recommendations.weekend_rn.title",
		descKey: "pbj_model.recommendations.weekend_rn.desc",
		wageKey: "weekendRn",
		impactedIds: ["1", "2", "3"],
		primaryCategoryId: "3",
		icon: <EventIcon sx={{ fontSize: 16 }} />,
		colorKey: "brandDeep",
		actionKey: "pbj_model.recommendations.weekend_rn.action",
		actionCatchupKey: "pbj_model.recommendations.weekend_rn.action_catchup",
		mathKey: "pbj_model.recommendations.weekend_rn.math",
		isWeekend: true,
	},
	{
		// Weekend CNA: extra CNA hours on weekends. Counts toward Total HPPD and
		// Weekend HPPD (not RN HPPD — CNAs aren't RNs).
		id: "weekend_cna",
		titleKey: "pbj_model.recommendations.weekend_cna.title",
		descKey: "pbj_model.recommendations.weekend_cna.desc",
		wageKey: "weekendCna",
		impactedIds: ["1", "3"],
		primaryCategoryId: "3",
		icon: <EventIcon sx={{ fontSize: 16 }} />,
		colorKey: "brandSoft",
		actionKey: "pbj_model.recommendations.weekend_cna.action",
		actionCatchupKey: "pbj_model.recommendations.weekend_cna.action_catchup",
		mathKey: "pbj_model.recommendations.weekend_cna.math",
		isWeekend: true,
	},
	{
		// LPN hours feed Total HPPD only (same coupling as CNA — LPNs aren't RNs).
		// Higher wage than CNA, so the BE greedy never picks LPN as a "Suggest plan"
		// step (cost-per-point loses to CNA); only exposed as a manual slider users
		// can dial in when staffing decisions favor LPN over CNA.
		id: "lpn",
		titleKey: "pbj_model.recommendations.lpn.title",
		descKey: "pbj_model.recommendations.lpn.desc",
		wageKey: "lpn",
		impactedIds: ["1"],
		primaryCategoryId: "1",
		icon: <PeopleIcon sx={{ fontSize: 16 }} />,
		colorKey: "info",
		actionKey: "pbj_model.recommendations.lpn.action",
		actionCatchupKey: "pbj_model.recommendations.lpn.action_catchup",
		mathKey: "pbj_model.recommendations.lpn.math",
		isWeekend: false,
	},
	{
		// Weekend LPN: extra LPN hours on weekends. Same coupling as Weekend CNA
		// (Total + Weekend HPPD, not RN HPPD). Manual slider only — greedy never
		// picks it because CNA is cheaper per HPPD point.
		id: "weekend_lpn",
		titleKey: "pbj_model.recommendations.weekend_lpn.title",
		descKey: "pbj_model.recommendations.weekend_lpn.desc",
		wageKey: "weekendLpn",
		impactedIds: ["1", "3"],
		primaryCategoryId: "3",
		icon: <EventIcon sx={{ fontSize: 16 }} />,
		colorKey: "info",
		actionKey: "pbj_model.recommendations.weekend_lpn.action",
		actionCatchupKey: "pbj_model.recommendations.weekend_lpn.action_catchup",
		mathKey: "pbj_model.recommendations.weekend_lpn.math",
		isWeekend: true,
	},
];
